import { useMemo, useState, type ReactNode } from "react";
import { useOrders } from "@/context/OrdersContext";
import { useBranch } from "@/context/BranchContext";
import Select from "@/components/base/Select";
import type { OrderStatus, PaymentMethod, PickupMethod, OrderSource } from "@/mocks/branch/orders";
import {
  TAB_FILTERS,
  DATE_FILTERS,
  PICKUP_METHODS,
  PAYMENT_METHODS,
  SOURCE_LABELS,
} from "./constants";
import OrderCard from "./components/OrderCard";
import OrderDetailPanel from "./components/OrderDetailPanel";
import ConfirmDialog from "./components/ConfirmDialog";
import AccessDenied from "@/pages/branch/access-denied/page";
import { canViewOrders } from "@/config/permissions";

type DemoState = "normal" | "empty" | "loading" | "error" | "lost";

const PAGE_SIZE = 6;

const DEMO_OPTIONS = [
  { value: "normal", label: "طبيعي" },
  { value: "loading", label: "تحميل البيانات" },
  { value: "empty", label: "لا توجد طلبات" },
  { value: "error", label: "خطأ في التحميل" },
  { value: "lost", label: "فقد الاتصال" },
];

export default function OrdersPage() {
  const { employee } = useBranch();
  const {
    orders,
    loading,
    error,
    selectedOrder,
    updateStatus,
    confirmNotPicked,
    rateCustomer,
    openOrderDetail,
    closeOrderDetail,
    retry,
    simulateError,
    simulateDisconnect,
  } = useOrders();

  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [paymentFilter, setPaymentFilter] = useState<PaymentMethod | "all">("all");
  const [pickupFilter, setPickupFilter] = useState<PickupMethod | "all">("all");
  const [sourceFilter, setSourceFilter] = useState<OrderSource | "all">("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [demoState, setDemoState] = useState<DemoState>("normal");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const o of orders) c[o.status] = (c[o.status] || 0) + 1;
    return c;
  }, [orders]);

  const countFor = (key: string): number => {
    if (key === "all") return orders.length;
    if (key === "completed") return (counts.picked_up || 0) + (counts.not_picked || 0);
    return counts[key] || 0;
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (dateFilter !== "today" ? 1 : 0) +
    (paymentFilter !== "all" ? 1 : 0) +
    (pickupFilter !== "all" ? 1 : 0) +
    (sourceFilter !== "all" ? 1 : 0);

  const dateOptions = DATE_FILTERS.map((d) => ({ value: d.key, label: d.label }));
  const paymentOptions = [
    { value: "all", label: "الكل" },
    ...(Object.keys(PAYMENT_METHODS) as PaymentMethod[]).map((m) => ({
      value: m,
      label: PAYMENT_METHODS[m].label,
      icon: PAYMENT_METHODS[m].icon,
    })),
  ];
  const pickupOptions = [
    { value: "all", label: "الكل" },
    ...(Object.keys(PICKUP_METHODS) as PickupMethod[]).map((m) => ({
      value: m,
      label: PICKUP_METHODS[m].label,
      icon: PICKUP_METHODS[m].icon,
    })),
  ];
  const sourceOptions = [
    { value: "all", label: "الكل" },
    ...(Object.keys(SOURCE_LABELS) as OrderSource[]).map((s) => ({
      value: s,
      label: SOURCE_LABELS[s].label,
      icon: SOURCE_LABELS[s].icon,
    })),
  ];

  const filtered = useMemo(() => {
    const tab = TAB_FILTERS.find((t) => t.key === filter);
    const q = search.trim();
    return orders.filter((o) => {
      if (tab && tab.statuses !== null && !tab.statuses.includes(o.status)) return false;
      if (paymentFilter !== "all" && o.paymentMethod !== paymentFilter) return false;
      if (pickupFilter !== "all" && o.pickupMethod !== pickupFilter) return false;
      if (sourceFilter !== "all" && o.source !== sourceFilter) return false;
      if (q.length > 0) {
        const hay = `${o.number} ${o.customerName || ""} ${o.customerPhone || ""}`;
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, filter, search, paymentFilter, pickupFilter, sourceFilter]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setSearch("");
    setDateFilter("today");
    setPaymentFilter("all");
    setPickupFilter("all");
    setSourceFilter("all");
  };

  const isLoading = loading || demoState === "loading";
  const isError = Boolean(error) || demoState === "error";
  const isEmpty = !isLoading && !isError && (demoState === "empty" || filtered.length === 0);

  if (!employee || !canViewOrders(employee.permissions)) {
    return <AccessDenied />;
  }

  return (
    <div className="max-w-[1400px] mx-auto">
      {selectedOrder ? (
        <OrderDetailPanel
          order={selectedOrder}
          onClose={closeOrderDetail}
          onAction={updateStatus}
          onConfirmNotPicked={confirmNotPicked}
          onRate={(id, stars) => rateCustomer(id, stars, employee?.name || "الموظف")}
        />
      ) : (
        <>
          {/* toolbar */}
          <div className="relative z-30 bg-background-50 rounded-card border border-background-200 p-3.5 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1 min-w-0">
                <div className="absolute top-1/2 -translate-y-1/2 right-3.5 w-5 h-5 flex items-center justify-center text-foreground-400 pointer-events-none">
                  <i className="ri-search-line text-lg"></i>
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setVisible(PAGE_SIZE);
                  }}
                  placeholder="ابحث برقم الطلب أو اسم العميل أو رقم الجوال..."
                  className="w-full h-11 rounded-btn border border-background-300 bg-background-100 pr-11 pl-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:bg-background-50 transition-all"
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
                <button
                  type="button"
                  onClick={() => setFiltersOpen((v) => !v)}
                  className={`inline-flex items-center justify-center gap-2 h-11 px-4 rounded-btn border text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto ${
                    filtersOpen || activeFilterCount > 0
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-background-300 text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <i className="ri-filter-3-line text-lg"></i>
                  الفلاتر
                  {activeFilterCount > 0 ? (
                    <span className="min-w-5 h-5 px-1.5 rounded-full bg-primary-500 text-white text-[11px] font-bold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </button>

                <div className="flex items-center gap-2 w-full sm:w-auto sm:pr-2 sm:border-r border-background-200">
                  <span className="text-xs font-semibold text-foreground-500 whitespace-nowrap hidden sm:inline">
                    معاينة الحالة
                  </span>
                  <Select
                    value={demoState}
                    options={DEMO_OPTIONS}
                    ariaLabel="معاينة حالات العرض"
                    className="w-full sm:w-44"
                    onChange={(v) => {
                      const next = v as DemoState;
                      setDemoState(next);
                      if (next === "error") simulateError();
                      if (next === "lost") simulateDisconnect();
                    }}
                  />
                </div>
              </div>
            </div>

            {filtersOpen ? (
              <div className="mt-3.5 pt-3.5 border-t border-background-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-slide-down">
                <FilterField label="تاريخ الطلب">
                  <Select
                    value={dateFilter}
                    options={dateOptions}
                    ariaLabel="تاريخ الطلب"
                    onChange={setDateFilter}
                  />
                </FilterField>
                <FilterField label="وسيلة الدفع">
                  <Select
                    value={paymentFilter}
                    options={paymentOptions}
                    ariaLabel="وسيلة الدفع"
                    onChange={(v) => setPaymentFilter(v as PaymentMethod | "all")}
                  />
                </FilterField>
                <FilterField label="طريقة الاستلام">
                  <Select
                    value={pickupFilter}
                    options={pickupOptions}
                    ariaLabel="طريقة الاستلام"
                    onChange={(v) => setPickupFilter(v as PickupMethod | "all")}
                  />
                </FilterField>
                <FilterField label="مصدر الطلب">
                  <Select
                    value={sourceFilter}
                    options={sourceOptions}
                    ariaLabel="مصدر الطلب"
                    onChange={(v) => setSourceFilter(v as OrderSource | "all")}
                  />
                  <span className="mt-1.5 flex items-start gap-1 text-[11px] leading-4 text-foreground-500">
                    <i className="ri-percent-line text-primary-600 mt-0.5 shrink-0"></i>
                    رسوم التطبيق تُحتسب فقط عند اختيار مصدر «تطبيق العميل»، ولا تُحتسب على طلبات POS داخل المطعم.
                  </span>
                </FilterField>
                <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between gap-3 pt-1">
                  <p className="text-xs text-foreground-500">
                    <span className="font-bold text-foreground-800">{filtered.length}</span> نتيجة
                    مطابقة من إجمالي {orders.length}
                  </p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="text-sm font-semibold text-primary-700 hover:underline cursor-pointer whitespace-nowrap"
                  >
                    مسح كل الفلاتر
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* status tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {TAB_FILTERS.map((tab) => {
              const active = filter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setFilter(tab.key);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`inline-flex items-center gap-1.5 shrink-0 h-10 px-3.5 rounded-full border text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "border-primary-500 bg-primary-500 text-white font-bold"
                      : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-foreground-900"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${
                      active ? "bg-white/25 text-white" : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {countFor(tab.key)}
                  </span>
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <OrderSkeleton />
          ) : isError ? (
            <div className="flex flex-col items-center justify-center text-center py-20">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-red-500"></i>
              </div>
              <h3 className="mt-4 text-lg font-bold text-foreground-900">تعذّر تحميل الطلبات</h3>
              <p className="mt-1.5 text-sm text-foreground-500 max-w-sm">
                {error || "حدث خطأ أثناء جلب البيانات — تحقق من الاتصال وحاول مجدداً"}
              </p>
              <button
                type="button"
                onClick={() => {
                  setDemoState("normal");
                  retry();
                }}
                className="mt-5 h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center text-center py-24">
              <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-archive-line text-4xl text-foreground-400"></i>
              </div>
              <h3 className="mt-5 text-lg font-bold text-foreground-900">
                {demoState === "empty" || filtered.length === 0 ? "لا توجد طلبات مطابقة" : "لا توجد طلبات"}
              </h3>
              <p className="mt-1.5 text-sm text-foreground-500 max-w-sm">
                جرّب تعديل الفلاتر أو مسحها — ستظهر الطلبات الجديدة هنا فور وصولها مع لافتة تنبيه.
              </p>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 h-11 px-6 rounded-btn border border-background-300 text-foreground-700 text-sm font-bold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  مسح الفلاتر
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {shown.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onOpen={openOrderDetail}
                    onAction={updateStatus}
                  />
                ))}
              </div>

              {visible < filtered.length ? (
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="h-12 px-6 rounded-btn border border-background-300 text-foreground-700 text-sm font-bold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    تحميل المزيد ({filtered.length - visible} متبقٍ)
                  </button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      {demoState === "lost" ? (
        <ConfirmDialog
          title="انقطع الاتصال"
          message="تعذّر الوصول لخدمة الطلبات لحظياً — يعيد النظام المحاولة تلقائياً. يمكنك المتابعة بالبيانات الحالية."
          confirmLabel="إعادة الاتصال"
          cancelLabel="حسناً"
          tone="danger"
          onConfirm={() => {
            setDemoState("normal");
            retry();
          }}
          onCancel={() => setDemoState("normal")}
        />
      ) : null}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block mb-1.5 text-xs font-semibold text-foreground-600">{label}</span>
      {children}
    </label>
  );
}

function OrderSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="bg-background-50 rounded-[24px] border border-background-200 p-5 flex flex-col gap-3 animate-pulse"
        >
          <div className="flex items-center justify-between">
            <div className="h-5 w-24 bg-background-200 rounded"></div>
            <div className="h-4 w-16 bg-background-200 rounded"></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-4 w-20 bg-background-200 rounded"></div>
            <div className="h-4 w-16 bg-background-200 rounded"></div>
          </div>
          <div className="flex items-center justify-between">
            <div className="h-4 w-24 bg-background-200 rounded"></div>
            <div className="h-4 w-16 bg-background-200 rounded"></div>
          </div>
          <div className="h-12 bg-background-200 rounded-btn"></div>
        </div>
      ))}
    </div>
  );
}